import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { api } from '../services/apiClient';
import { useCart } from '../context/CartContext';
import { useSettings } from '../hooks/useSettings';

const ArtworkDetailPage = () => {
  const { id } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItem, openCart } = useCart();
  const { settings } = useSettings();

  const fetchArtwork = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.illustrations.get(id);
      setArtwork(data);
    } catch (err) {
      setError('Opera non trovata');
      console.error('Error fetching artwork:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArtwork();
  }, [fetchArtwork]);

  const handleAddToCart = () => {
    addItem(artwork);
    openCart();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-light pt-20 md:pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-dark"></div>
      </div>
    );
  }

  if (error || !artwork) {
    return (
      <div className="min-h-screen bg-brand-light pt-20 md:pt-24 flex flex-col items-center justify-center px-4">
        <p className="text-brand-dark text-lg mb-6">{error || 'Opera non trovata'}</p>
        <Link
          to="/galleria"
          className="inline-flex items-center gap-2 text-brand-dark font-medium hover:text-brand-main transition-colors"
        >
          <ArrowLeft size={18} />
          Torna alla Galleria
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-light pt-20 md:pt-24 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Back button */}
        <Link
          to="/galleria"
          className="inline-flex items-center gap-2 text-brand-dark font-medium hover:text-brand-main transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          Torna alla Galleria
        </Link>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image */}
          <div className="rounded-lg overflow-hidden bg-brand-light shadow-lg">
            <img
              src={artwork.image_url || 'https://via.placeholder.com/500'}
              alt={artwork.name}
              className="w-full max-h-[80vh] object-contain"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col">
            {/* Category */}
            {artwork.categories?.name && (
              <span className="inline-block w-fit px-3 py-1 rounded-full bg-brand-main/30 text-brand-dark text-xs font-medium mb-4">
                {artwork.categories.name}
              </span>
            )}

            {/* Title */}
            <h1 className="font-display text-5xl md:text-6xl italic font-light text-brand-ink mb-4">
              {artwork.name}
            </h1>

            {/* Description */}
            <p className="font-body text-lg text-brand-muted leading-relaxed mb-8">
              {artwork.description || 'Nessuna descrizione disponibile'}
            </p>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-6 py-8 border-y border-brand-main/20 mb-8">
              {settings.show_prices && (
                <div>
                  <p className="font-body text-xs text-brand-muted uppercase tracking-wider mb-2">
                    Prezzo
                  </p>
                  <p className="font-display text-3xl font-bold text-brand-dark">
                    €{Number(artwork.selling_price).toFixed(2)}
                  </p>
                </div>
              )}
              <div>
                <p className="font-body text-xs text-brand-muted uppercase tracking-wider mb-2">
                  Disponibilità
                </p>
                <p className={`font-display text-lg font-semibold ${
                  artwork.total_stock > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {artwork.total_stock > 0 ? 'Disponibile' : 'Non disponibile'}
                </p>
              </div>
              {artwork.formats && (
                <div>
                  <p className="font-body text-xs text-brand-muted uppercase tracking-wider mb-2">
                    Formato
                  </p>
                  <p className="font-body text-brand-dark">
                    {artwork.formats.code} — {artwork.formats.name}
                  </p>
                </div>
              )}
              {artwork.creation_date && (
                <div>
                  <p className="font-body text-xs text-brand-muted uppercase tracking-wider mb-2">
                    Data di Creazione
                  </p>
                  <p className="font-body text-brand-dark">
                    {new Date(artwork.creation_date).toLocaleDateString('it-IT', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Add to cart button */}
            <button
              onClick={handleAddToCart}
              disabled={artwork.total_stock === 0}
              className={`w-full py-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                artwork.total_stock > 0
                  ? 'bg-brand-dark text-white hover:bg-opacity-90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Plus size={20} />
              {artwork.total_stock > 0 ? 'Aggiungi al Carrello' : 'Non Disponibile'}
            </button>

            {/* Share info */}
            <p className="font-body text-sm text-brand-muted text-center mt-6">
              Ti è piaciuta? Condividila con i tuoi amici!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkDetailPage;
